// src/pages/VehicleV2/BatteryCardEntry.jsx

import React, { useEffect, useMemo, useState, useContext, useCallback } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaSave, FaTimes, FaPlus, FaArrowLeft } from "react-icons/fa";

const API_BASE = "http://nissi-india.com/api/batteryCard";

const toInt = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : def;
};

const dmyToIso = (dmy) => {
  if (!dmy) return "";
  const s = String(dmy).trim();
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(s);
  if (!m) return "";
  return `${m[3]}-${m[2]}-${m[1]}`;
};

const emptyForm = {
  batteryId: 0,
  batteryCode: "",
  dateOfReceipt: "",
  vehicleId: "",
  vehicleRegNoId: "",
  batterySerialNo: "",
  batteryType: "",
  batteryMake: "",
  expectedLifeYear: "Select",
  expectedLifeMonth: "Select",
  purchasedFromId: "0",
  purchaseAmt: "",
  purchasedDate: "",
  warrantyFrom: "",
  warrantyTo: "",
  remarks: "",
  storeId: "",
  srcSerialNo: "",
};

const BatteryCardEntry = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const batteryIdFromUrl = useMemo(
    () => toInt(searchParams.get("batteryId")),
    [searchParams]
  );
  const isEdit = batteryIdFromUrl > 0;
  const isView = searchParams.get("view") === "1";

  const loginUserId =
    localStorage.getItem("userId") || localStorage.getItem("loginuserid") || "";
  const branchId = Number(
    localStorage.getItem("branchId") || localStorage.getItem("branchid") || 8
  );

  const finYear =
    localStorage.getItem("finYear") ||
    localStorage.getItem("finyear") ||
    localStorage.getItem("financialyear") ||
    sessionStorage.getItem("finyear") ||
    "";

  const finYearId =
    localStorage.getItem("finYearId") ||
    localStorage.getItem("finyearid") ||
    localStorage.getItem("financialyearid") ||
    sessionStorage.getItem("finyearid") ||
    "";

  const requestConfig = useMemo(() => {
    const headers = {
      "x-branchid": branchId,
      "x-userid": loginUserId,
    };
    if (finYear) headers["x-finyear"] = finYear;
    if (finYearId) {
      headers["x-finyearid"] = finYearId;
      headers["x-finyear-id"] = finYearId;
    }
    return { headers };
  }, [finYear, finYearId, branchId, loginUserId]);

  const [suppliers, setSuppliers] = useState([]);
  const [stores, setStores] = useState([]);
  const [vehicleCodes, setVehicleCodes] = useState([]);
  const [vehicleRegNos, setVehicleRegNos] = useState([]);
  const [expectedLifeYears, setExpectedLifeYears] = useState(["Select"]);
  const [expectedLifeMonths, setExpectedLifeMonths] = useState(["Select"]);
  const [showSrcSerialNo, setShowSrcSerialNo] = useState(true);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(emptyForm);

  const inputClass = `w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
    isDark
      ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
  } ${isView ? "opacity-80 cursor-not-allowed" : ""}`;

  const labelClass = `block mb-2 text-sm font-medium ${
    isDark ? "text-gray-300" : "text-gray-700"
  }`;

  const setField = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const loadDropdowns = useCallback(async () => {
    if (!branchId) return;
    const { data } = await axios.get(`${API_BASE}/dropdowns`, {
      params: { branchId },
      ...requestConfig,
    });
    if (!data?.success) throw new Error(data?.message || "Failed to load dropdowns");
    setSuppliers(data.suppliers || []);
    setStores(data.stores || []);
    setVehicleCodes(data.vehicleCodes || []);
    setVehicleRegNos(data.vehicleRegNos || []);
    setExpectedLifeYears(data.expectedLifeYears || ["Select"]);
    setExpectedLifeMonths(data.expectedLifeMonths || ["Select"]);
    setShowSrcSerialNo(!!data.showSrcSerialNo);
  }, [branchId, requestConfig]);

  const loadNewCode = useCallback(async () => {
    const { data } = await axios.get(`${API_BASE}/code`, {
      params: { loginUserId },
      ...requestConfig,
    });
    if (!data?.success) throw new Error(data?.message || "Failed to generate code");
    setField("batteryCode", data.batteryCode || "");
  }, [loginUserId, requestConfig]);

  const loadVehicleRegNos = useCallback(
    async (vehicleId) => {
      const { data } = await axios.get(`${API_BASE}/vehicle-regno`, {
        params: { vehicleId },
        ...requestConfig,
      });
      if (!data?.success) throw new Error(data?.message || "Failed to load vehicle reg no");
      setVehicleRegNos(data.vehicleRegNos || []);
    },
    [requestConfig]
  );

  const loadForEdit = useCallback(
    async (batteryId) => {
      const { data } = await axios.get(`${API_BASE}/view`, {
        params: { batteryId },
        ...requestConfig,
      });
      if (!data?.success) throw new Error(data?.message || "Failed to load battery");
      const d = data.data || {};

      setFormData((prev) => ({
        ...prev,
        batteryId: toInt(d.fld_batteryid),
        batteryCode: d.fld_batterycode || "",
        dateOfReceipt: dmyToIso(d.dateOfReceipt || d.fld_dateofreceipt),
        vehicleId: String(d.fld_vehicleid ?? ""),
        vehicleRegNoId: String(d.fld_vehicleid ?? ""),
        batterySerialNo: d.fld_battery_serialno || "",
        batteryType: d.fld_batterytype || "",
        batteryMake: d.fld_batterymake || "",
        expectedLifeYear: d.fld_expectedlifeyear || "Select",
        expectedLifeMonth: d.fld_expectedlifemonth || "Select",
        purchasedFromId: String(d.fld_purchasedfrom ?? "0"),
        purchaseAmt: d.fld_purchaseamt ?? "",
        purchasedDate: dmyToIso(d.purchasedDate || d.fld_purchaseddate),
        warrantyFrom: dmyToIso(d.warrentyFrom),
        warrantyTo: dmyToIso(d.warrentyTo),
        remarks: d.fld_remarks || "",
        storeId: String(d.fld_storeid ?? ""),
        srcSerialNo: d.fld_srcserialno || "",
      }));

      if (d.fld_vehicleid) await loadVehicleRegNos(d.fld_vehicleid);
    },
    [loadVehicleRegNos, requestConfig]
  );

  const loadSerialDetails = useCallback(
    async (serialNo) => {
      if (!serialNo) return;
      const { data } = await axios.get(`${API_BASE}/serial-details`, {
        params: { serialNo, branchId },
        ...requestConfig,
      });
      if (!data?.success) throw new Error(data?.message || "Failed to load serial details");
      const d = data.data;
      if (!d) return;
      setFormData((prev) => ({
        ...prev,
        batteryMake: d.batteryMake || prev.batteryMake,
        purchaseAmt: d.purchaseAmt || prev.purchaseAmt,
        purchasedDate: dmyToIso(d.issuedDate) || prev.purchasedDate,
        srcSerialNo: d.srcSerialNo || prev.srcSerialNo,
        purchasedFromId: d.supplier?.id ? String(d.supplier.id) : prev.purchasedFromId,
      }));
    },
    [branchId, requestConfig]
  );

  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        setLoading(true);
        await loadDropdowns();
        if (ignore) return;
        if (isEdit) {
          await loadForEdit(batteryIdFromUrl);
        } else {
          setFormData((prev) => ({ ...prev, batteryId: 0 }));
          await loadNewCode();
        }
      } catch (err) {
        toast.error(err?.message || "Failed to load");
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    return () => {
      ignore = true;
    };
  }, [batteryIdFromUrl, isEdit, loadDropdowns, loadForEdit, loadNewCode]);

  const handleInputChange = async (e) => {
    const { name, value } = e.target;
    if (isView) return;
    setField(name, value);

    if (name === "vehicleId") {
      setField("vehicleRegNoId", value);
      if (value && value !== "Select") {
        try {
          await loadVehicleRegNos(value);
        } catch (err) {
          toast.error(err.message);
        }
      } else {
        setVehicleRegNos([]);
      }
    }

    if (name === "vehicleRegNoId") setField("vehicleId", value);
  };

  const handleSerialBlur = async () => {
    if (isView) return;
    try {
      await loadSerialDetails(formData.batterySerialNo);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const validate = () => {
    if (!formData.dateOfReceipt) return "Date of Receipt is required";
    if (!formData.vehicleId) return "Vehicle Code is required";
    if (!formData.batterySerialNo) return "Battery Serial No is required";
    if (!formData.batteryType) return "Battery Type is required";
    if (!formData.batteryMake) return "Battery Make is required";
    if (!formData.storeId) return "Store is required";
    if (!formData.expectedLifeYear || formData.expectedLifeYear === "Select")
      return "Expected Life In Years is required";
    if (!formData.expectedLifeMonth || formData.expectedLifeMonth === "Select")
      return "Expect Life In Months is required";
    if (!formData.warrantyFrom) return "Warranty Period From is required";
    if (!formData.warrantyTo) return "Warranty Period To is required";
    return "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isView) return;
    const msg = validate();
    if (msg) {
      toast.error(msg);
      return;
    }

    try {
      setLoading(true);
      const payload = {
        batteryId: isEdit ? batteryIdFromUrl : 0,
        batteryCode: formData.batteryCode,
        branchId,
        storeId: toInt(formData.storeId),
        vehicleId: toInt(formData.vehicleId),
        batterySerialNo: formData.batterySerialNo,
        batteryType: formData.batteryType,
        batteryMake: formData.batteryMake,
        expectedLifeYear: formData.expectedLifeYear,
        expectedLifeMonth: formData.expectedLifeMonth,
        purchasedFromId: toInt(formData.purchasedFromId),
        purchaseAmt: formData.purchaseAmt,
        purchasedDate: formData.purchasedDate,
        dateOfReceipt: formData.dateOfReceipt,
        warrantyFrom: formData.warrantyFrom,
        warrantyTo: formData.warrantyTo,
        remarks: formData.remarks,
        srcSerialNo: formData.srcSerialNo,
        loginUserId,
      };

      const { data } = await axios.post(`${API_BASE}/save`, payload, requestConfig);
      if (!data?.success) throw new Error(data?.message || "Save failed");
      toast.success(data.message || "Saved");

      const savedId = toInt(data.batteryId);
      if (!isEdit && savedId > 0) {
        navigate(`/vehicle/BatteryCardEntry?batteryId=${savedId}`, { replace: true });
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Save failed");
    } finally {
      setLoading(false);
    }
  };

  const handleResetNew = async () => {
    try {
      setLoading(true);
      setVehicleRegNos([]);
      setFormData(emptyForm);
      await loadNewCode();
      navigate(`/vehicle/BatteryCardEntry`, { replace: true });
    } catch (err) {
      toast.error(err?.message || "Failed to reset");
    } finally {
      setLoading(false);
    }
  };

  const title = isView
    ? "Battery Card View"
    : isEdit
    ? "Battery Card Edit"
    : "Battery Card Entry";

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">BATTERY CARD</span>}
          setSkin={setSkin}
          currentSkin={currentSkin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <form onSubmit={handleSubmit}>
            <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
              <div>
                <h2 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                  {title}
                </h2>
                <p className={`text-sm mt-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  {isEdit || isView
                    ? `Battery Code: ${formData.batteryCode || "-"}`
                    : "Battery code is generated automatically"}
                </p>
              </div>

              <div className="flex items-center gap-3 flex-wrap">
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg ${
                    isDark
                      ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                      : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                  }`}
                >
                  <FaArrowLeft />
                  Back
                </button>

                {!isView && (
                  <button
                    type="submit"
                    disabled={loading}
                    className={`flex items-center gap-2 px-5 py-3 rounded-lg font-semibold shadow-lg text-white ${
                      loading ? "opacity-60 cursor-not-allowed" : ""
                    } ${buttonClass} ${hoverClass}`}
                  >
                    <FaSave />
                    {loading ? "Saving..." : isEdit ? "Update" : "Save"}
                  </button>
                )}
              </div>
            </div>

            <Section title="Battery Details" isDark={isDark}>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <Field label="Battery Code" labelClass={labelClass}>
                  <input type="text" value={formData.batteryCode} className={inputClass} readOnly />
                </Field>

                <Field label="Date of Receipt *" labelClass={labelClass}>
                  <input
                    type="date"
                    name="dateOfReceipt"
                    value={formData.dateOfReceipt}
                    onChange={handleInputChange}
                    className={inputClass}
                    disabled={isView}
                  />
                </Field>

                <Field label="Store *" labelClass={labelClass}>
                  <select
                    name="storeId"
                    value={formData.storeId}
                    onChange={handleInputChange}
                    className={inputClass}
                    disabled={isView}
                  >
                    <option value="">Select</option>
                    {stores.map((s) => (
                      <option key={s.id} value={String(s.id)}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </Field>

                <Field label="Vehicle Code *" labelClass={labelClass}>
                  <select
                    name="vehicleId"
                    value={formData.vehicleId}
                    onChange={handleInputChange}
                    className={inputClass}
                    disabled={isView}
                  >
                    <option value="">Select</option>
                    {vehicleCodes.map((v) => (
                      <option key={v.id} value={String(v.id)}>
                        {v.code}
                      </option>
                    ))}
                  </select>
                </Field>

                <Field label="Vehicle Reg No" labelClass={labelClass}>
                  <select
                    name="vehicleRegNoId"
                    value={formData.vehicleRegNoId}
                    onChange={handleInputChange}
                    className={inputClass}
                    disabled={isView}
                  >
                    <option value="">Select</option>
                    {vehicleRegNos.map((x) => (
                      <option key={`${x.id}-${x.regNo}`} value={String(x.id)}>
                        {x.regNo}
                      </option>
                    ))}
                  </select>
                </Field>

                <Field label="Battery Serial No *" labelClass={labelClass}>
                  <input
                    type="text"
                    name="batterySerialNo"
                    value={formData.batterySerialNo}
                    onChange={handleInputChange}
                    onBlur={handleSerialBlur}
                    className={inputClass}
                    disabled={isView}
                  />
                </Field>

                <Field label="Battery Type *" labelClass={labelClass}>
                  <input
                    type="text"
                    name="batteryType"
                    value={formData.batteryType}
                    onChange={handleInputChange}
                    className={inputClass}
                    disabled={isView}
                  />
                </Field>

                <Field label="Battery Make *" labelClass={labelClass}>
                  <input
                    type="text"
                    name="batteryMake"
                    value={formData.batteryMake}
                    onChange={handleInputChange}
                    className={inputClass}
                    disabled={isView}
                  />
                </Field>

                {showSrcSerialNo && (
                  <Field label="SRC Serial No" labelClass={labelClass}>
                    <input
                      type="text"
                      name="srcSerialNo"
                      value={formData.srcSerialNo}
                      onChange={handleInputChange}
                      className={inputClass}
                      disabled={isView}
                    />
                  </Field>
                )}
              </div>
            </Section>

            <Section title="Life & Warranty" isDark={isDark}>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <Field label="Expected Life In Years *" labelClass={labelClass}>
                  <select
                    name="expectedLifeYear"
                    value={formData.expectedLifeYear}
                    onChange={handleInputChange}
                    className={inputClass}
                    disabled={isView}
                  >
                    {expectedLifeYears.map((y) => (
                      <option key={y} value={y}>
                        {y === "Select" ? "Select" : y}
                      </option>
                    ))}
                  </select>
                </Field>

                <Field label="Expect Life In Months *" labelClass={labelClass}>
                  <select
                    name="expectedLifeMonth"
                    value={formData.expectedLifeMonth}
                    onChange={handleInputChange}
                    className={inputClass}
                    disabled={isView}
                  >
                    {expectedLifeMonths.map((m) => (
                      <option key={m} value={m}>
                        {m === "Select" ? "Select" : m}
                      </option>
                    ))}
                  </select>
                </Field>

                <Field label="Warranty Period From *" labelClass={labelClass}>
                  <input
                    type="date"
                    name="warrantyFrom"
                    value={formData.warrantyFrom}
                    onChange={handleInputChange}
                    className={inputClass}
                    disabled={isView}
                  />
                </Field>

                <Field label="Warranty Period To *" labelClass={labelClass}>
                  <input
                    type="date"
                    name="warrantyTo"
                    value={formData.warrantyTo}
                    onChange={handleInputChange}
                    className={inputClass}
                    disabled={isView}
                  />
                </Field>
              </div>
            </Section>

            <Section title="Purchase Details" isDark={isDark}>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <Field label="Purchase From" labelClass={labelClass}>
                  <select
                    name="purchasedFromId"
                    value={formData.purchasedFromId}
                    onChange={handleInputChange}
                    className={inputClass}
                    disabled={isView}
                  >
                    <option value="0">Select</option>
                    {suppliers.map((s) => (
                      <option key={s.id} value={String(s.id)}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </Field>

                <Field label="Purchased Amount" labelClass={labelClass}>
                  <input
                    type="number"
                    name="purchaseAmt"
                    value={formData.purchaseAmt}
                    onChange={handleInputChange}
                    className={inputClass}
                    disabled={isView}
                  />
                </Field>

                <Field label="Issued / Purchased Date" labelClass={labelClass}>
                  <input
                    type="date"
                    name="purchasedDate"
                    value={formData.purchasedDate}
                    onChange={handleInputChange}
                    className={inputClass}
                    disabled={isView}
                  />
                </Field>

                <div className="md:col-span-2 lg:col-span-3">
                  <Field label="Remarks" labelClass={labelClass}>
                    <textarea
                      name="remarks"
                      value={formData.remarks}
                      onChange={handleInputChange}
                      className={inputClass}
                      rows="3"
                      disabled={isView}
                    />
                  </Field>
                </div>
              </div>
            </Section>

            <div className="flex justify-between items-center mt-2 pt-6 border-t border-gray-200 dark:border-gray-700 flex-wrap gap-3">
              <div className="flex gap-2">
                {!isEdit && !isView && (
                  <button
                    type="button"
                    onClick={handleResetNew}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border ${
                      isDark
                        ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-gray-100"
                    }`}
                  >
                    <FaPlus />
                    New
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border ${
                    isDark
                      ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                      : "border-gray-300 text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  <FaTimes />
                  Cancel
                </button>
              </div>

              {!isView && (
                <button
                  type="submit"
                  disabled={loading}
                  className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg text-white ${
                    loading ? "opacity-60 cursor-not-allowed" : ""
                  } ${buttonClass} ${hoverClass}`}
                >
                  <FaSave />
                  {isEdit ? "Update" : "Save"}
                </button>
              )}
            </div>
          </form>

          <ToastContainer />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

function Section({ title, isDark, children }) {
  return (
    <div className={`p-6 rounded-lg mb-6 ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`}>
      <h3 className={`text-lg font-semibold mb-4 ${isDark ? "text-white" : "text-blue-900"}`}>
        {title}
      </h3>
      {children}
    </div>
  );
}

function Field({ label, labelClass, children }) {
  return (
    <div>
      <label className={labelClass}>{label}</label>
      {children}
    </div>
  );
}

export default BatteryCardEntry;